/* import __COLOCATED_TEMPLATE__ from './fin-readiness-personalized-section.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { use } from 'ember-resources/util/function-resource';
import {
  filters,
  and,
} from 'embercom/components/reporting/bespoke/overview/conversation-funnel/series-config';

interface Args {
  reportState: ReportState;
  setTotalVolume: (totalVolume: string) => void;
}

export default class FinReadinessPersonalizedSection extends Component<Args> {
  @service declare appService: AppService;
  @service declare store: Store;

  @use convWithQueryTypeResource = staticDataResource(this, {
    serializedChart: () => this.convWithQueryTypeChartDefinition,
    reportState: () => this.args.reportState,
  });

  @use convWithQueryTypeTeammateOnlyResource = staticDataResource(this, {
    serializedChart: () => this.convWithQueryTypeTeammateOnlyChartDefinition,
    reportState: () => this.args.reportState,
  });

  @use conversationsWithQueryTypeTeammateChatbotResource = staticDataResource(this, {
    serializedChart: () => this.conversationsWithQueryTypeTeammateChatbotChartDefinition,
    reportState: () => this.args.reportState,
  });

  get queryTypeFilter() {
    return {
      type: 'exists',
      data: {
        property: 'conversation_custom_fields#ai_insights_query_type',
        attribute: 'conversation_custom_fields#ai_insights_query_type',
      },
    };
  }

  get teammateOnlyWithQueryTypeFilter() {
    return and(this.queryTypeFilter, filters.teammateWasOnlyInvolved);
  }

  get teammateChatbotWithQueryTypeFilter() {
    return and(
      this.queryTypeFilter,
      filters.teammateWasEverInvolved,
      filters.chatbotWasEverInvolved,
      filters.finWasNeverInvolved,
    );
  }

  get convWithQueryTypeChartDefinition() {
    return {
      visualization_type: 'counter',
      view_by: null,
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          filters: {
            type: 'and',
            filters: [this.queryTypeFilter],
          },
        },
      ],
    };
  }

  get convWithQueryTypeTeammateOnlyChartDefinition() {
    return {
      visualization_type: 'counter',
      view_by: null,
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          filters: {
            type: 'and',
            filters: [this.teammateOnlyWithQueryTypeFilter],
          },
        },
      ],
    };
  }

  get conversationsWithQueryTypeTeammateChatbotChartDefinition() {
    return {
      visualization_type: 'counter',
      view_by: null,
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          filters: {
            type: 'and',
            filters: [this.teammateChatbotWithQueryTypeFilter],
          },
        },
      ],
    };
  }

  get convWithQueryTypeValue() {
    let value = this.convWithQueryTypeResource.formatValue({
      value: this.convWithQueryTypeResource.kpiData[0],
      metricId: this.convWithQueryTypeChartDefinition.chart_series[0].metric_id,
    });
    this.args.setTotalVolume(value);
    return value;
  }

  get handledPercentage() {
    let handledWithQuery = this.teammateChatbotTotal;
    let totalWithQuery = this.convWithQueryTypeResource.kpiData[0] as number;
    return ((handledWithQuery / totalWithQuery) * 100).toFixed(0);
  }

  get teammateChatbotTotal() {
    if (this.isLoading) {
      return 0;
    }
    let teammateHandledWithQuery = this.convWithQueryTypeTeammateOnlyResource.kpiData[0] as number;
    let teammateChatbotHandledWithQuery = this.conversationsWithQueryTypeTeammateChatbotResource
      .kpiData[0] as number;
    return teammateHandledWithQuery + teammateChatbotHandledWithQuery;
  }

  get isLoading() {
    return (
      this.convWithQueryTypeResource.isLoading ||
      this.convWithQueryTypeTeammateOnlyResource.isLoading ||
      this.conversationsWithQueryTypeTeammateChatbotResource.isLoading
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::FinReadiness::FinReadinessPersonalizedSection': typeof FinReadinessPersonalizedSection;
  }
}
